import Chart, { useChart } from 'src/components/chart';
import { useTheme, alpha, Box, Stack, Typography, Skeleton } from '@mui/material';
// Localisation
import { useLocales } from "src/locales";
import { fDate } from 'src/utils/format-time';
import CiCard from './CiCard';
import CustomTooltip from './CustomTooltip';


interface WeeklyDataPoint {
  week_start: string;
  solo_bidder: number | null;
  top_ad_position: number | null;
  organic_only: number | null;
  compet_only: number | null;
  isPlaceholder?: boolean;
}

interface BlendedPresenceRateCardProps {
  title: string;
  dateRange: string;
  mainContentTitle?: string;
  topMetric?: string;
  weeklySeries?: WeeklyDataPoint[];
  loading?: boolean;
  isPlaceholder?: boolean;
  /** When true, render only header + content (non-brand structure) for use inside non-brand card wrapper + footer */
  contentOnly?: boolean;
}

export default function BlendedPresenceRateCard(props: BlendedPresenceRateCardProps) {
  const { t } = useLocales()
  const theme = useTheme()


  const MAX_WEEKS = 12;

  const weeklyData = props.weeklySeries ?? [];

  const normalizedWeeks: WeeklyDataPoint[] = weeklyData
    .slice(0, MAX_WEEKS)
    .map((item) => {
      const hasValues =
        item.organic_only !== null &&
        item.solo_bidder !== null &&
        item.top_ad_position !== null &&
        item.compet_only !== null;

      if (!hasValues) {
        return {
          ...item,
          organic_only: 0,
          solo_bidder: 0,
          top_ad_position: 0,
          compet_only: 0,
          isPlaceholder: true,
        };
      }

      return {
        ...item,
        isPlaceholder: false,
      };
    });

  const missingWeeksCount = Math.max(
    0,
    MAX_WEEKS - normalizedWeeks.length
  );

  const chartWeeks: WeeklyDataPoint[] = [
    ...normalizedWeeks,
    ...Array.from(
      { length: missingWeeksCount },
      (_, index): WeeklyDataPoint => ({
        week_start: `placeholder-${index}`,
        organic_only: 0,
        solo_bidder: 0,
        top_ad_position: 0,
        compet_only: 0,
        isPlaceholder: true,
      })
    ),
  ];

  const PLACEHOLDER_HEIGHTS = [
    46,
    68,
    83,
    57,
    74,
    50,
    81,
    63,
    77,
    54,
    71,
    59,
  ];

  const organicOnlyData = chartWeeks.map((item) =>
    item.isPlaceholder ? 0 : Number(item.organic_only) || 0
  );

  const soloBidderData = chartWeeks.map((item) =>
    item.isPlaceholder ? 0 : Number(item.solo_bidder) || 0
  );

  const topAdPositionData = chartWeeks.map((item) =>
    item.isPlaceholder ? 0 : Number(item.top_ad_position) || 0
  );

  const competitorOnlyData = chartWeeks.map((item) =>
    item.isPlaceholder ? 0 : Number(item.compet_only) || 0
  );

  /**
   * Les colonnes grises ont une hauteur fixe.
   * Les vraies semaines ont une valeur de placeholder égale à zéro.
   */
  const placeholderData = chartWeeks.map((item, index) =>
    item.isPlaceholder ? PLACEHOLDER_HEIGHTS[index % PLACEHOLDER_HEIGHTS.length] : 0
  );

  // Calculate current values for tooltip (from last week or average)
  const lastWeek = weeklyData[weeklyData.length - 1];
  const currentOrganicOnly = lastWeek?.organic_only || 0;
  const currentSoloBidder = lastWeek?.solo_bidder || 0;
  const currentTopAdPosition = lastWeek?.top_ad_position || 0;
  const currentCompetitorOnly = lastWeek?.compet_only || 0;
  const currentBlendedPresenceRate = currentOrganicOnly + currentSoloBidder + currentTopAdPosition;

  const series = [
    {
      name: t('dashboard.global.visibility.organic_only'),
      data: organicOnlyData,
      icon: 'solar:global-linear',
      color: '#18A055',
      value: currentOrganicOnly,
    },
    {
      name: t('dashboard.global.visibility.solo_bidder'),
      data: soloBidderData,
      color: '#C09910',
      value: currentSoloBidder,
    },
    {
      name: t('dashboard.global.visibility.top_ad_position'),
      data: topAdPositionData,
      color: '#F9DE81',
      value: currentTopAdPosition,
    },
    {
      name: 'Placeholder',
      data: placeholderData,
      color: 'rgba(145, 158, 171, 0.12)',
    },
  ];

  const topMetricValue = props.topMetric || (currentBlendedPresenceRate > 0 ? `${Math.round(currentBlendedPresenceRate)}%` : undefined);
  const topMetric = props.loading ? undefined : (topMetricValue ?? "N/A");
  const hasTopMetric = Boolean(topMetricValue);

  // Treat top metric as valid data even when weekly series is missing.
  const hasWeeklyValues = weeklyData.some(
    (d) =>
      Number(d.organic_only) > 0 ||
      Number(d.solo_bidder) > 0 ||
      Number(d.top_ad_position) > 0 ||
      Number(d.compet_only) > 0
  );

  const hasNoData = !hasWeeklyValues && !hasTopMetric;

  const chartOptions = useChart({
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false,
      },
      parentHeightOffset: 0,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '20%',
        borderRadius: 1,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        hideZeroBarsWhenGrouped: false,
      },
    },
    // apexcharts 4.x insets each stacked segment by half the stroke width, so the
    // useChart default stroke (width 3, meant for line charts) leaves white seams
    // between segments — the stroke must be fully disabled here.
    stroke: {
      show: false,
      width: 0,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    colors: ['#18A055', '#C09910', '#F9DE81', '#EEF1F4'],
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        if (dataPointIndex < 0 || dataPointIndex >= chartWeeks.length) {
          return '';
        }

        const weekData = chartWeeks[dataPointIndex];

        // Aucun tooltip sur les placeholders gris
        if (!weekData || weekData.isPlaceholder) {
          return '';
        }

        const organicValue = weekData?.organic_only || 0;
        const soloBidderValue = weekData?.solo_bidder || 0;
        const topAdPositionValue = weekData?.top_ad_position || 0;

        const isDark = theme.palette.mode === 'dark';
        const bgColor = isDark
          ? alpha(theme.palette.grey[800], 0.95)
          : alpha(theme.palette.background.paper, 0.95);
        const textColor = theme.palette.text.primary;
        const textSecondaryColor = theme.palette.text.secondary;
        const borderColor = theme.palette.divider;
        const fontSize = theme.typography.pxToRem(14);
        const lineHeight = 1.5;

        const formattedDate = weekData?.week_start ? fDate(weekData.week_start) : '';

        let tooltipHtml = `
          <div style="
            background-color: ${bgColor};
            color: ${textColor};
            width: 320px!important;
            font-size: ${fontSize};
            line-height: ${lineHeight};
            box-shadow: ${isDark
            ? '0 4px 12px rgba(0, 0, 0, 0.3)'
            : '0 4px 12px rgba(0, 0, 0, 0.1)'
          };
            font-family: 'Public Sans', sans-serif;
          ">
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 14px 16px 0 16px;
              gap: 16px;
            ">
              <span style="
                color: #333C47;
                font-family: 'Public Sans', sans-serif;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 14px;
                white-space: nowrap;
              ">
                ${props.mainContentTitle ?? 'Presence breakdown'}
              </span>

              ${formattedDate
            ? `
                    <span style="
                      color: #637381;
                      text-align: right;
                      font-family: 'Public Sans', sans-serif;
                      font-size: 14px;
                      font-style: normal;
                      font-weight: 400;
                      line-height: 14px;
                      white-space: nowrap;
                    ">
                      ${formattedDate}
                    </span>
                  `
            : ''
          }
            </div>

            <div style="
              display: flex;
              flex-direction: column;
              gap: 4px;
              padding: 12px 16px;
            ">
              <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                min-height: 22px;
              ">
                <div style="
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  min-height: 22px;
                ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    style="
                      display: block;
                      flex-shrink: 0;
                      align-self: center;
                    "
                  >
                    <g fill="none" stroke="#18A055" stroke-width="1.5">
                      <path d="M22 12a10 10 0 1 1-20.001 0A10 10 0 0 1 22 12Z"/>
                      <path d="M16 12c0 1.313-.104 2.614-.305 3.827c-.2 1.213-.495 2.315-.867 3.244c-.371.929-.812 1.665-1.297 2.168c-.486.502-1.006.761-1.531.761s-1.045-.259-1.53-.761c-.486-.503-.927-1.24-1.298-2.168c-.372-.929-.667-2.03-.868-3.244A23.6 23.6 0 0 1 8 12c0-1.313.103-2.614.304-3.827s.496-2.315.868-3.244c.371-.929.812-1.665 1.297-2.168C10.955 2.26 11.475 2 12 2s1.045.259 1.53.761c.486.503.927 1.24 1.298 2.168c.372.929.667 2.03.867 3.244C15.897 9.386 16 10.687 16 12Z"/>
                      <path stroke-linecap="round" d="M2 12h20"/>
                    </g>
                  </svg>

                  <span style="
                    color: #333C47;
                    font-family: 'Public Sans', sans-serif;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 22px;
                  ">
                    ${t('dashboard.global.visibility.organic_only')}
                  </span>
                </div>

              <span style="
                color: #637381;
                font-variant-numeric: lining-nums tabular-nums;
                font-family: 'Public Sans', sans-serif;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 22px;
                margin-left: 12px;
                white-space: nowrap;
              ">
                ${Math.round(organicValue * 10) / 10}%
              </span>
              </div>

              <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                min-height: 22px;
              ">
                <div style="
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  min-height: 22px;
                ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 32 32"
                    style="
                      display: block;
                      flex-shrink: 0;
                      align-self: center;
                    "
                  >
                    <path
                      fill="#C09910"
                      d="M16 4a5 5 0 1 1-5 5a5 5 0 0 1 5-5m0-2a7 7 0 1 0 7 7a7 7 0 0 0-7-7m10 28h-2v-5a5 5 0 0 0-5-5h-6a5 5 0 0 0-5 5v5H6v-5a7 7 0 0 1 7-7h6a7 7 0 0 1 7 7z"
                    />
                  </svg>

                  <span style="
                    color: #333C47;
                    font-family: 'Public Sans', sans-serif;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 22px;
                  ">
                    ${t('dashboard.global.visibility.solo_bidder')}
                  </span>
                </div>

              <span style="
                color: #637381;
                font-variant-numeric: lining-nums tabular-nums;
                font-family: 'Public Sans', sans-serif;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 22px;
                margin-left: 12px;
                white-space: nowrap;
              ">
                  ${Math.round(soloBidderValue * 10) / 10}%
                </span>
              </div>

              <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                min-height: 22px;
              ">
                <div style="
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  min-height: 22px;
                ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 32 32"
                    style="
                      display: block;
                      flex-shrink: 0;
                      align-self: center;
                    "
                  >
                    <path
                      fill="#F9DE81"
                      d="M30 30h-2v-5a5.006 5.006 0 0 0-5-5v-2a7.01 7.01 0 0 1 7 7zm-8 0h-2v-5a5.006 5.006 0 0 0-5-5H9a5.006 5.006 0 0 0-5 5v5H2v-5a7.01 7.01 0 0 1 7-7h6a7.01 7.01 0 0 1 7 7zM20 2v2a5 5 0 0 1 0 10v2a7 7 0 0 0 0-14m-8 2a5 5 0 1 1-5 5a5 5 0 0 1 5-5m0-2a7 7 0 1 0 7 7a7 7 0 0 0-7-7"
                    />
                  </svg>

                  <span style="
                    color: #333C47;
                    font-family: 'Public Sans', sans-serif;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 22px;
                  ">
                    ${t('dashboard.global.visibility.top_ad_position')}
                  </span>
                </div>

              <span style="
                color: #637381;
                font-variant-numeric: lining-nums tabular-nums;
                font-family: 'Public Sans', sans-serif;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 22px;
                margin-left: 12px;
                white-space: nowrap;
              ">
                  ${Math.round(topAdPositionValue * 10) / 10}%
                </span>
              </div>
            </div>
          </div>
        `;
        return tooltipHtml;
      }
    },
    yaxis: {
      show: false,
      min: 0,
      max: 100,
    },
    xaxis: {
      type: 'category',
      categories: chartWeeks.map((d) => d.week_start),

      crosshairs: {
        show: true,
        width: 16,
        position: 'back',
        fill: {
          type: 'solid',
          color: 'rgba(145, 158, 171, 0.12)',
        },
        stroke: {
          width: 0,
        },
      },

      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    legend: {
      show: false
    },
    grid: {
      show: false,
      padding: { left: 0, right: 0, top: 8, bottom: 4 },
    },
  });

  function mainContent() {
    if (hasNoData) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px',
            marginY: '30px',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            {t('dashboard.global.no_data_available')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
            {t('dashboard.global.visibility.no_results_found_description')}
          </Typography>
        </Box>
      );
    }

    if (weeklyData.length === 0) {
      return (
        <Box sx={{ width: "100%", height: "65px" }} />
      );
    }

    return (
      <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>
        <Chart
          type="bar"
          series={series}
          options={chartOptions}
          sx={{ width: '100%' }}
          height={105}
        />
      </Box>
    )
  }

  // Header like non-brand visibility: indicator line + label on left, value on right
  const titleWithIndicator = (
    <Typography className="card-top-header-item-label" component="span" variant="h6" color="text.primary" sx={{ fontSize: 14, fontWeight: 400, color: '#333C47', lineHeight: 1, fontFamily: 'Public Sans, sans-serif' }}>
      {props.title}
    </Typography>
  );

  const visibilityRateTooltip = t('dashboard.global.visibility.tooltips.visibilityRate');

  if (props.contentOnly) {
    const chartBody =
      hasNoData ? (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <Typography variant="body2" color="text.secondary">{t('dashboard.global.no_data_available')}</Typography>
        </Stack>
      ) : weeklyData.length === 0 ? (
        <Box sx={{ width: '100%', height: '100%' }} />
      ) : (
        <Chart type="bar" series={series} options={chartOptions} sx={{ width: '100%' }} height={'100%'} />
      );
    return (
      <>
        <Stack
          className="card-top-header"
          borderBottom={1}
          borderColor="divider"
          direction="row"
          px={2}
          height={40}
          minHeight={40}
          maxHeight={40}
          boxSizing="border-box"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            className="card-top-header-item"
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {titleWithIndicator}
            </Box>

            <CustomTooltip title={visibilityRateTooltip} placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                    stroke="#637381"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </CustomTooltip>

            <Typography
              className="card-top-header-item-value"
              variant="h6"
              color="text.primary"
            >
              {topMetric}
            </Typography>
          </Stack>
        </Stack>
        {props.loading ? (
          <Skeleton variant="rectangular" width="100%" height={127} />
        ) : (
          <Stack
            width="100%"
            height={127}
            minHeight={127}
            maxHeight={127}
            flex="0 0 127px"
            px={2}
            pb={0.5}
            sx={{
              overflow: 'visible',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {chartBody}
          </Stack>
        )}
      </>
    );
  }


  return (
    <CiCard
      dateRange={props.dateRange}
      title={titleWithIndicator}
      titleTooltip={visibilityRateTooltip}
      topMetric={topMetric}
      mainContent={mainContent()}
      loading={props.loading}
    />
  )
}
